import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, MessageSquare, GitBranch, Star, Settings, Play,
  Send, Plus, Loader2, PenSquare, Clock, X, Image as ImageIcon,
} from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../useAuth';
import WorkflowService from '../../services/workflow.service';
import ChatService from '../../services/chat.service';
import ImageService from '../../services/image.service';
import AssistantConfigureView from '../AssistantsView/AssistantConfigureView';
import RunsView from '../Workflows/RunsView';
import RunInspector from '../Workflows/RunInspector';
import { useAssistantContext } from '../../contexts/AssistantContext';
import useToast from '../../hooks/useToast';
import ToastContainer from '../ui/ToastContainer';
import MobileMenuButton from "../Sidebar/MobileMenuButton";
import { useHeaderSlot } from "../Sidebar/MobileNavContext";
import ZenitThinking from '../ui/ZenitThinking';
import MarkdownRenderer from '../markdown/MarkdownRenderer';
import { useAutoResize, isLongText, LongTextCard } from '../Chat/chatHelpers';

/* ─── helpers ─────────────────────────────────────────── */
const AVATAR_BG = [
  'bg-primary', 'bg-blue-600', 'bg-emerald-600',
  'bg-orange-600', 'bg-rose-600', 'bg-cyan-600', 'bg-amber-600',
];
function avatarBg(name) {
  return name ? AVATAR_BG[name.charCodeAt(0) % AVATAR_BG.length] : AVATAR_BG[0];
}
function getInitial(name) {
  return name ? name.charAt(0).toUpperCase() : '?';
}

/* ─── chat attachments ────────────────────────────────── */
const MAX_CHAT_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

// Whether this agent is allowed to receive files/images in chat.
function agentAcceptsFiles(agent) {
  return Boolean(
    agent?.config?.acceptsFilesAndImages ??
    agent?.config?.accepts_files_and_images ??
    agent?.acceptsFilesAndImages
  );
}

function formatAttachmentSize(size) {
  if (!size && size !== 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const readFileAsArrayBuffer = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsArrayBuffer(file);
});

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

// Read a File into the content-block shape the chat API expects (image → data
// URL, anything else → base64). Mirrors the HomeView/Zeno attachment flow.
async function prepareAttachmentFromFile(file) {
  let contentBlock;
  let previewUrl = null;

  if (file.type.startsWith('image/')) {
    const dataUrl = await readFileAsDataUrl(file);
    previewUrl = dataUrl;
    contentBlock = {
      type: 'image',
      data: { url: dataUrl, mime_type: file.type, filename: file.name },
      order: 1,
    };
  } else {
    const arrayBuffer = await readFileAsArrayBuffer(file);
    contentBlock = {
      type: 'file',
      data: {
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        content: arrayBufferToBase64(arrayBuffer),
      },
      order: 1,
    };
  }

  return { name: file.name, size: file.size, mimeType: file.type || 'application/octet-stream', previewUrl, contentBlock };
}

// Combine the typed text and prepared attachments into ordered content blocks.
function buildUserContentBlocks(text, atts) {
  const blocks = [];
  if (text) blocks.push({ type: 'text', data: { text }, order: blocks.length + 1 });
  (atts || []).forEach((att) => {
    if (att?.contentBlock) blocks.push({ ...att.contentBlock, order: blocks.length + 1 });
  });
  return blocks;
}

/* ─── WorkflowsTab ─────────────────────────────────────── */
const HealthBar = ({ pct }) => {
  const color =
    pct >= 90 ? 'from-emerald-500 to-emerald-400' :
    pct >= 70 ? 'from-yellow-500 to-yellow-400' : 'from-rose-500 to-rose-400';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden border border-white/10">
        <div className={`h-full bg-gradient-to-r ${color} transition-all`} style={{ width: `${pct ?? 0}%` }} />
      </div>
      <span className="text-xs text-white tabular-nums w-9 text-right">{pct ?? 0}%</span>
    </div>
  );
};

const WorkflowsTab = ({ projectId, agentId, agentName, accountId, onOpenWorkflow, onCreateWorkflow }) => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId || !accountId || !agentId) return;
    setLoading(true);

    const load = async () => {
      try {
        // 1. Fetch all workflows for the project (light — no definitions)
        const listRes = await WorkflowService.listWorkflows({
          account_id: accountId,
          project_id: projectId,
          limit: 100,
          include_definition: false,
        });

        if (!listRes.success) return;

        const all = listRes.data.workflows || [];
        if (all.length === 0) { setWorkflows([]); return; }

        // 2. For each workflow, fetch its assigned agents in parallel
        const agentsResults = await Promise.all(
          all.map(wf =>
            WorkflowService.listWorkflowAgents(wf.workflow_id || wf.id, { source: 'draft' })
          )
        );

        // 3. Keep only workflows that include this agent, then normalize fields
        const matched = all
          .filter((_, i) => {
            const res = agentsResults[i];
            return res.success && res.data.agent_ids.includes(agentId);
          })
          .map(wf => ({
            id: wf.workflow_id || wf.id,
            name: wf.name,
            description: wf.description || '',
            status: wf.status,
            published_version_number: wf.published_version_number,
            has_draft: wf.has_draft_changes || wf.has_draft || false,
            health_percentage: wf.stats?.success_rate ?? wf.health_percentage ?? 0,
            total_runs: wf.total_runs || 0,
          }));

        setWorkflows(matched);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [projectId, accountId, agentId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-white">
            {agentName} Workflows
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {workflows.length} workflow{workflows.length !== 1 ? 's' : ''} where this agent participates
            {/* The "Shared" legend only means something once there are rows to
                label — otherwise it ran straight into "…participates". */}
            {workflows.length > 0 && (
              <>
                {'. '}
                <span className="text-purple-400">Shared</span>
                {' = involves multiple agents.'}
              </>
            )}
          </p>
        </div>
        {/* Icon-only on phones, like the other create buttons. */}
        <button
          type="button"
          onClick={() => onCreateWorkflow?.()}
          aria-label="Create workflow"
          title="Create workflow"
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center gap-2 rounded-xl bg-primary text-white transition-colors hover:bg-violet-700 text-sm font-medium sm:h-10 sm:w-auto sm:px-4"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Create workflow</span>
        </button>
      </div>

      {workflows.length === 0 ? (
        <div className="flex-1 flex items-center justify-center border border-dashed border-white/10 rounded-xl">
          <p className="text-gray-500 text-sm">No workflows associated with this agent</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Table header */}
          <div className="grid grid-cols-[2fr,100px,80px,180px,60px] gap-4 px-4 py-2 text-[10px] font-semibold tracking-wider text-gray-600 uppercase border-b border-white/5">
            <span>Workflow</span>
            <span>Status</span>
            <span>Versión</span>
            <span>Health (30d)</span>
            <span className="text-right">Runs</span>
          </div>

          {workflows.map((wf) => (
            <div
              key={wf.id}
              onClick={() => onOpenWorkflow?.(wf)}
              className="grid grid-cols-[2fr,100px,80px,180px,60px] gap-4 items-center px-4 py-3.5 border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors group"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate group-hover:text-purple-300 transition-colors">
                  {wf.name}
                </p>
                <p className="text-xs text-gray-500 truncate">{wf.description}</p>
              </div>
              <div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                  wf.status === 'active'
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                    : wf.status === 'draft'
                    ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    : 'bg-zinc-700/50 text-zinc-400 border-zinc-600/30'
                }`}>
                  {wf.status || 'draft'}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-400 bg-white/5 px-2 py-0.5 rounded-md border border-white/10">
                  v{wf.published_version_number ?? 0}
                </span>
                {wf.has_draft && (
                  <span className="ml-1 text-[10px] text-yellow-400">draft</span>
                )}
              </div>
              <div className="min-w-0">
                <HealthBar pct={wf.health_percentage ?? 0} />
                <p className="text-[10px] text-gray-600 mt-0.5">{wf.total_runs ?? 0} runs</p>
              </div>
              <div className="text-right">
                <span className="text-sm text-white font-medium">{wf.total_runs ?? 0}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── useAgentChat — shared state & logic ─────────────── */
function useAgentChat(agent, accountId, userId) {
  const [messages, setMessages]           = useState([]);
  const [input, setInput]                 = useState('');
  const [sending, setSending]             = useState(false);
  const [chatHistoryId, setChatHistoryId] = useState(null);
  const [streaming, setStreaming]         = useState('');
  // Open by default only where there is room for it; on phones the 256px rail
  // would leave the chat with almost nothing.
  const [showHistory, setShowHistory]     = useState(
    () => typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches
  );
  const [histories, setHistories]         = useState([]);
  const [loadingHist, setLoadingHist]     = useState(false);
  const [attachments, setAttachments]     = useState([]);
  const bottomRef  = useRef(null);
  const streamCtrl = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  useEffect(() => {
    if (!showHistory || !accountId || !agent?.id) return;
    setLoadingHist(true);
    ChatService.listChatHistories({ accountId, assistantId: agent.id, limit: 30 })
      .then((res) => { if (res.success) setHistories(Array.isArray(res.data) ? res.data : res.data?.chatHistories || []); })
      .finally(() => setLoadingHist(false));
  }, [showHistory, accountId, agent?.id]);

  const extractContent = (m) => {
    if (m.content && typeof m.content === 'string') return m.content;
    if (m.text) return m.text;
    const blocks = m.contentBlocks || m.content_blocks || [];
    const raw = blocks.map(b => b.data?.text || b.text || '').join('');
    if (raw && m.role === 'assistant') {
      try { const p = JSON.parse(raw); if (p.response) return p.response; } catch { /* plain */ }
    }
    return raw;
  };

  const startNewChat = useCallback(() => {
    setChatHistoryId(null);
    setMessages([]);
    setStreaming('');
  }, []);

  const loadHistory = useCallback(async (hist) => {
    setChatHistoryId(hist.id);
    setMessages([]);
    setStreaming('');
    const res = await ChatService.getChatMessages(hist.id, { limit: 100 });
    if (res.success) {
      const msgs = Array.isArray(res.data) ? res.data : (res.data?.messages || []);
      setMessages(msgs.map((m) => {
        const blocks = m.contentBlocks || m.content_blocks || [];
        const imageBlocks = blocks.filter(b => b.type === 'image');
        return {
          role: m.role || (m.sender === 'user' ? 'user' : 'assistant'),
          content: extractContent(m),
          displayBlocks: imageBlocks.length > 0 ? imageBlocks : undefined,
        };
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    const atts = attachments;
    if ((!text && atts.length === 0) || sending) return;
    setInput('');
    setAttachments([]);
    setSending(true);
    const userBlocks = buildUserContentBlocks(text, atts);
    const imageBlocks = atts.filter(a => a.contentBlock?.type === 'image').map(a => a.contentBlock);
    setMessages((p) => [...p, { role: 'user', content: text, displayBlocks: imageBlocks.length ? imageBlocks : undefined }]);
    setStreaming('');

    let histId = chatHistoryId;
    if (!histId) {
      const res = await ChatService.createChatHistory({
        accountId, userId, assistantId: agent.id, initialMessage: text || null,
      });
      if (!res.success) {
        setMessages((p) => [...p, { role: 'assistant', content: 'Could not start chat session.' }]);
        setSending(false);
        return;
      }
      histId = res.data?.id || res.data?.history_id || res.data?.chat_history_id;
      setChatHistoryId(histId);
      setHistories((p) => [res.data, ...p]);
    }

    let accumulated = '';
    let displayBlocks = [];
    streamCtrl.current = ChatService.sendMessageStream(
      { assistantId: agent.id, chatHistoryId: histId, message: text, contentBlocks: userBlocks, accountId, userId },
      {
        onContent: (chunk) => { accumulated += chunk; setStreaming(accumulated); },
        onMetadata: (meta) => {
          const blocks = meta?.display_blocks || meta?.displayBlocks || [];
          if (Array.isArray(blocks) && blocks.length > 0) displayBlocks = blocks;
        },
        onDone: () => {
          let final = accumulated;
          try { const p = JSON.parse(accumulated); if (p.response) final = p.response; } catch { /* plain */ }
          setMessages((p) => [...p, { role: 'assistant', content: final, displayBlocks }]);
          setStreaming(''); setSending(false);
        },
        onError: () => {
          setMessages((p) => [...p, { role: 'assistant', content: 'Something went wrong. Please try again.' }]);
          setStreaming(''); setSending(false);
        },
      }
    );
  }, [input, attachments, sending, chatHistoryId, agent?.id, accountId, userId]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  const unwrapStreaming = (raw) => {
    try { const p = JSON.parse(raw); return p.response || raw; } catch { return raw; }
  };

  return {
    messages, input, setInput, sending, chatHistoryId, streaming,
    showHistory, setShowHistory, histories, loadingHist,
    attachments, setAttachments,
    bottomRef, startNewChat, handleSend, handleKeyDown, loadHistory, unwrapStreaming,
  };
}

/* ─── Image lightbox ───────────────────────────────────── */
const ImageLightbox = ({ url, onClose }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 120);
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `generated-image-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(href);
    } catch {
      window.open(url, '_blank');
    }
  };

  // Portal to <body>: the chat lives inside a `transform`-ed container, which
  // would otherwise trap this `fixed` overlay and stop it covering the screen.
  return createPortal((
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: visible ? 'rgba(0,0,0,0.82)' : 'rgba(0,0,0,0)', transition: 'background 0.12s ease' }}
      onClick={handleClose}
    >
      <div
        className="relative max-w-[90vw] max-h-[90vh]"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'scale(1)' : 'scale(0.94)', transition: 'opacity 0.12s ease, transform 0.12s ease' }}
        onClick={e => e.stopPropagation()}
      >
        <img src={url} alt="generated" className="max-w-[90vw] max-h-[85vh] rounded-2xl object-contain shadow-2xl" />
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-medium backdrop-blur-sm transition-colors duration-150"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            Download
          </button>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-7 h-7 rounded-lg bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm transition-colors duration-150"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};

/* ─── Generated image with fade-in ────────────────────── */
const GeneratedImage = ({ url, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={url}
      alt="generated"
      onLoad={() => setLoaded(true)}
      onClick={onClick}
      className="mt-2 rounded-xl max-w-full max-h-80 object-contain cursor-pointer hover:opacity-90 transition-opacity duration-150"
      style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.4s ease' }}
    />
  );
};

/* ─── Shared message bubble ────────────────────────────── */
const MsgBubble = ({ msg, agent, compact }) => {
  const bg = avatarBg(agent?.name);
  const isUser = msg.role === 'user';
  const [lightboxUrl, setLightboxUrl] = useState(null);

  return (
    <>
      {lightboxUrl && <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
      {/* ChatGPT-style: the user message is a right-aligned bubble while the
          agent reply spans the full column width (no bubble), so both share
          the same right edge instead of drifting apart. */}
      <div className={`w-full flex min-w-0 opacity-0 animate-reveal-up ${isUser ? 'justify-end' : 'justify-start'}`}>
        {isUser ? (
          <div className={`${compact ? 'max-w-[92%] text-xs px-3 py-2' : 'max-w-[80%] text-sm px-4 py-3'} min-w-0 rounded-2xl rounded-br-sm bg-primary text-white leading-relaxed break-words overflow-hidden`}>
            {msg.content && (isLongText(msg.content)
              ? <LongTextCard text={msg.content} />
              : <p className="whitespace-pre-wrap">{msg.content}</p>)}
            {msg.displayBlocks?.map((block, i) =>
              block.type === 'image' && block.data?.url
                ? <GeneratedImage key={i} url={block.data.url} onClick={() => setLightboxUrl(block.data.url)} />
                : null
            )}
          </div>
        ) : (
          <div className={`w-full flex items-start min-w-0 ${compact ? 'gap-2' : 'gap-3'}`}>
            <div className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} rounded-xl ${bg} flex-shrink-0 flex items-center justify-center text-white font-bold`}
              style={{ fontSize: compact ? 10 : 14 }}>
              {agent?.avatar
                ? <img src={agent.avatar} alt={agent?.name} className="w-full h-full object-cover rounded-xl" />
                : getInitial(agent?.name)}
            </div>
            <div className={`flex-1 min-w-0 ${compact ? 'text-xs' : 'text-sm'} text-gray-100 leading-relaxed break-words overflow-hidden`}>
              <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-semibold text-purple-300 mb-1`}>{agent?.name}</p>
              <MarkdownRenderer markdown={msg.content} />
              {msg.displayBlocks?.map((block, i) =>
                block.type === 'image' && block.data?.url
                  ? <GeneratedImage key={i} url={block.data.url} onClick={() => setLightboxUrl(block.data.url)} />
                  : null
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/* ─── Shared history panel ─────────────────────────────── */
const HistoryPanel = ({ chatState }) => {
  const { showHistory, setShowHistory, loadingHist, histories, chatHistoryId, loadHistory, startNewChat } = chatState;
  const now = new Date();
  const dayStart = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const diffDays = (d) => Math.floor((dayStart(now) - dayStart(d)) / 86400000);
  const relLabel = (d) => {
    const diff = diffDays(new Date(d));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const grouped = histories.reduce((acc, h) => {
    const ts = h.updatedAt || h.updated_at || h.createdAt || h.created_at;
    const diff = ts ? diffDays(new Date(ts)) : 99;
    const g = diff === 0 ? 'Today' : diff === 1 ? 'Yesterday' : 'Earlier';
    if (!acc[g]) acc[g] = [];
    acc[g].push(h);
    return acc;
  }, {});

  return (
    // Below lg the rail overlays the chat instead of stealing 256px from it.
    <div
      className={`absolute inset-y-0 right-0 z-30 flex flex-col overflow-hidden border-l border-white/8 bg-[#0d0a14] transition-all duration-300 ease-in-out lg:static lg:z-auto lg:flex-shrink-0 lg:bg-white/[0.02] ${
        showHistory ? 'w-64' : 'w-0'
      }`}
    >
      <div className="w-64 flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 flex-shrink-0">
          <span className="text-xs font-semibold text-white whitespace-nowrap">Chat history</span>
          <div className="flex items-center gap-1">
            <button onClick={startNewChat} title="New chat" className="p-1 text-gray-500 hover:text-white transition-colors">
              <Plus className="w-4 h-4" />
            </button>
            <button onClick={() => setShowHistory(false)} className="p-1 text-gray-500 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {loadingHist ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
            </div>
          ) : histories.length === 0 ? (
            <p className="text-xs text-gray-600 text-center py-6 whitespace-nowrap">No past chats</p>
          ) : (
            ['Today', 'Yesterday', 'Earlier'].filter(g => grouped[g]).map((group) => (
              <div key={group}>
                <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-4 pt-3 pb-1 whitespace-nowrap">{group}</p>
                {grouped[group].map((h) => {
                  const ts = h.updatedAt || h.updated_at;
                  const msgCount = h.totalMessages ?? h.total_messages;
                  const tokens   = h.totalTokens ?? h.total_tokens;
                  return (
                    <button key={h.id} onClick={() => loadHistory(h)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors ${h.id === chatHistoryId ? 'bg-purple-500/10' : ''}`}>
                      <p className="text-xs text-white truncate whitespace-nowrap">{h.title || 'Untitled'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {msgCount != null && <span className="text-[10px] text-gray-500">{msgCount} msg{msgCount !== 1 ? 's' : ''}</span>}
                        {tokens   != null && <span className="text-[10px] text-gray-600">{tokens > 1000 ? `${Math.round(tokens/1000)}k` : tokens} tok</span>}
                        {ts && !msgCount  && <span className="text-[10px] text-gray-600">{relLabel(ts)}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Thinking bubble ──────────────────────────────────── */
const ThinkingBubble = ({ agent, compact }) => {
  const bg = avatarBg(agent?.name);
  return (
    <div className="flex items-end gap-2 flex-row opacity-0 animate-reveal-up">
      <div className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} rounded-xl ${bg} flex-shrink-0 flex items-center justify-center text-white font-bold`}
        style={{ fontSize: compact ? 10 : 14 }}>
        {agent?.avatar
          ? <img src={agent.avatar} alt={agent?.name} className="w-full h-full object-cover rounded-xl" />
          : getInitial(agent?.name)}
      </div>
      <div className={`bg-white/[0.07] border border-white/10 rounded-2xl rounded-bl-sm ${compact ? 'px-3 py-2' : 'px-4 py-3'} flex items-center gap-2`}>
        <ZenitThinking size={compact ? 14 : 18} />
        <span className={`${compact ? 'text-xs' : 'text-sm'} text-gray-400`}>Thinking…</span>
      </div>
    </div>
  );
};

/* ─── StreamingBubble ──────────────────────────────────── */
const StreamingBubble = ({ agent, content, compact }) => {
  const bg = avatarBg(agent?.name);
  const unwrapped = (() => { try { const p = JSON.parse(content); return p.response || content; } catch { return content; } })();
  return (
    <div className={`w-full flex items-start min-w-0 ${compact ? 'gap-2' : 'gap-3'}`}>
      <div className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} rounded-xl ${bg} flex-shrink-0 flex items-center justify-center text-white font-bold`}
        style={{ fontSize: compact ? 10 : 14 }}>
        {agent?.avatar
          ? <img src={agent.avatar} alt={agent?.name} className="w-full h-full object-cover rounded-xl" />
          : getInitial(agent?.name)}
      </div>
      <div className={`flex-1 min-w-0 ${compact ? 'text-xs' : 'text-sm'} text-gray-100 leading-relaxed`}>
        <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-semibold text-purple-300 mb-1`}>{agent?.name}</p>
        <p className="whitespace-pre-wrap break-words">{unwrapped}</p>
        <span className="inline-block w-1 h-3.5 bg-purple-400 animate-pulse ml-0.5 align-text-bottom" />
      </div>
    </div>
  );
};

/* ─── AgentChatCenter ──────────────────────────────────── */
const AgentChatCenter = ({ agent, chatState }) => {
  const { messages, input, setInput, sending, streaming, showHistory, setShowHistory, bottomRef, startNewChat, handleSend, handleKeyDown, attachments, setAttachments } = chatState;
  const hasMessages = messages.length > 0 || !!streaming;
  const inputRef = useAutoResize(input, 160);
  const fileRef = useRef(null);
  const [attachError, setAttachError] = useState('');
  const canAttach = agentAcceptsFiles(agent);
  const canSend = (!!input.trim() || attachments.length > 0) && !sending;

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = '';
    if (files.length === 0) return;

    const oversized = files.filter(f => f.size > MAX_CHAT_ATTACHMENT_SIZE_BYTES);
    const valid = files.filter(f => f.size <= MAX_CHAT_ATTACHMENT_SIZE_BYTES);
    setAttachError(oversized.length
      ? `Some files exceed the 10 MB limit and were skipped: ${oversized.map(f => f.name).join(', ')}`
      : '');
    if (valid.length === 0) return;

    try {
      const prepared = await Promise.all(valid.map(prepareAttachmentFromFile));
      setAttachments(prev => [...prev, ...prepared]);
    } catch {
      setAttachError('One or more files could not be prepared.');
    }
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="flex h-full overflow-hidden">
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex-1 overflow-y-auto px-6 py-8">
          {!hasMessages ? (
            <div className="flex flex-col items-center justify-center h-full gap-2">
              <p className="text-gray-400 text-[15px] font-medium">Start the conversation!</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 max-w-4xl mx-auto">
              {messages.map((msg, i) => <MsgBubble key={i} msg={msg} agent={agent} compact={false} />)}
              {sending && !streaming && <ThinkingBubble agent={agent} compact={false} />}
              {streaming && <StreamingBubble agent={agent} content={streaming} compact={false} />}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <div className="px-6 pb-6 flex-shrink-0">
          <div className="max-w-4xl mx-auto">
            {canAttach && (
              <input ref={fileRef} type="file" multiple className="hidden" onChange={onFilesSelected} />
            )}
            {attachError && (
              <p className="text-xs text-red-400 mb-2">{attachError}</p>
            )}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {attachments.map((att, idx) => (
                  <div key={`${att.name}-${idx}`} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5">
                    {att.previewUrl
                      ? <img src={att.previewUrl} alt={att.name} className="h-8 w-8 rounded object-cover border border-white/10" />
                      : <div className="h-8 w-8 rounded bg-white/10 grid place-items-center text-[9px] font-semibold text-gray-300">FILE</div>}
                    <div className="min-w-0">
                      <p className="text-xs text-white max-w-[150px] truncate">{att.name}</p>
                      <p className="text-[10px] text-gray-500">{formatAttachmentSize(att.size)}</p>
                    </div>
                    <button onClick={() => removeAttachment(idx)} className="text-gray-500 hover:text-white transition-colors" aria-label="Remove attachment">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-3 bg-white/[0.06] border border-white/10 rounded-2xl px-4 py-3 focus-within:border-purple-500/40 transition-colors shadow-lg">
              {canAttach && (
                <button onClick={() => fileRef.current?.click()} disabled={sending} title="Attach files & images"
                  className="text-gray-600 hover:text-gray-400 transition-colors flex-shrink-0 mb-0.5 disabled:opacity-40">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </button>
              )}
              <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                placeholder="Type your message..." rows={1} disabled={sending}
                className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 resize-none focus:outline-none disabled:opacity-50 leading-relaxed"
                style={{ maxHeight: '160px', overflowY: 'auto' }} />
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => setShowHistory((p) => !p)} title="Chat history"
                  className={`transition-colors ${showHistory ? 'text-purple-400' : 'text-gray-600 hover:text-gray-400'}`}>
                  <Clock className="w-5 h-5" />
                </button>
                <button onClick={startNewChat} title="New chat" className="text-gray-600 hover:text-gray-400 transition-colors">
                  <PenSquare className="w-5 h-5" />
                </button>
                <button onClick={handleSend} disabled={!canSend}
                  className="w-9 h-9 rounded-xl bg-primary hover:bg-violet-700 text-white flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-purple-600/30">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HistoryPanel chatState={chatState} />
    </div>
  );
};

/* ─── AgentChatRail ────────────────────────────────────── */
const AgentChatRail = ({ agent, chatState }) => {
  const { messages, input, setInput, sending, streaming, bottomRef, handleSend, handleKeyDown } = chatState;
  const bg = avatarBg(agent?.name);
  const inputRef = useAutoResize(input, 100);

  return (
    <div className="flex flex-col h-full border-l border-white/8 bg-white/[0.02]">
      <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-white/8 flex-shrink-0">
        <div className={`w-7 h-7 rounded-lg ${bg} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 relative`}>
          {agent?.avatar
            ? <img src={agent.avatar} alt={agent?.name} className="w-full h-full object-cover rounded-lg" />
            : getInitial(agent?.name)}
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#0d0d0d] bg-emerald-400" />
        </div>
        <p className="text-xs font-semibold text-white truncate flex-1">Test {agent?.name}</p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messages.length === 0 && !streaming && (
          <div className="text-center py-8">
            <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center text-white text-lg font-bold mx-auto mb-2`}>
              {agent?.avatar
                ? <img src={agent.avatar} alt={agent?.name} className="w-full h-full object-cover rounded-xl" />
                : getInitial(agent?.name)}
            </div>
            <p className="text-xs text-gray-500">Hi! I'm {agent?.name}. How can I help you today?</p>
          </div>
        )}
        {messages.map((msg, i) => <MsgBubble key={i} msg={msg} agent={agent} compact={true} />)}
        {sending && !streaming && <ThinkingBubble agent={agent} compact={true} />}
        {streaming && <StreamingBubble agent={agent} content={streaming} compact={true} />}
        <div ref={bottomRef} />
      </div>

      <div className="px-3 py-3 border-t border-white/8 flex-shrink-0">
        <div className="flex items-end gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 focus-within:border-purple-500/50 transition-colors">
          <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
            placeholder={`Message ${agent?.name}…`} rows={1} disabled={sending}
            className="flex-1 bg-transparent text-xs text-white placeholder-gray-600 resize-none focus:outline-none disabled:opacity-50 leading-relaxed"
            style={{ maxHeight: '100px', overflowY: 'auto' }} />
          <button onClick={handleSend} disabled={!input.trim() || sending}
            className="flex-shrink-0 w-6 h-6 rounded-lg bg-primary hover:bg-violet-700 text-white flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Gallery thumbnail with fade-in ──────────────────── */
const GalleryThumb = ({ img, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <button
      onClick={onClick}
      className="group relative aspect-square rounded-xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-purple-500/40 transition-colors"
    >
      <img
        src={img.url}
        alt="generated"
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
        style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.4s ease, transform 0.2s ease' }}
      />
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-4 h-4 text-purple-400/60 animate-spin" />
        </div>
      )}
    </button>
  );
};

/* ─── GalleryTab ───────────────────────────────────────── */
const PAGE_SIZE = 50;

const GalleryTab = ({ agentId, agentName, accountId }) => {
  const [images, setImages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [offset, setOffset]   = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const load = useCallback(async (nextOffset) => {
    if (!accountId || !agentId) return;
    if (nextOffset === 0) setLoading(true);
    setError(false);

    const res = await ImageService.listImagesByAssistant({
      accountId,
      assistantId: agentId,
      status: 'completed',
      sort: 'created_at:desc',
      limit: PAGE_SIZE,
      offset: nextOffset,
    });

    if (res.success) {
      const batch = res.data || [];
      setImages((prev) => (nextOffset === 0 ? batch : [...prev, ...batch]));
      setHasMore(batch.length === PAGE_SIZE);
      setOffset(nextOffset + batch.length);
    } else {
      setError(true);
    }
    setLoading(false);
  }, [accountId, agentId]);

  // URLs presigned expiran a las 24h: re-fetch al montar / cambiar de agent
  useEffect(() => {
    setImages([]);
    setOffset(0);
    setHasMore(false);
    load(0);
  }, [load]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {lightboxUrl && <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-white">{agentName} Gallery</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {images.length > 0
              ? `${images.length} image${images.length !== 1 ? 's' : ''} generated by this agent`
              : 'Images generated by this agent'}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 border border-dashed border-white/10 rounded-xl">
          <p className="text-gray-500 text-sm">Could not load images</p>
          <button
            onClick={() => load(0)}
            className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : images.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center border border-dashed border-white/10 rounded-xl">
          <ImageIcon className="w-8 h-8 text-purple-400/40 mb-3" />
          <p className="text-gray-500 text-sm">No images generated yet</p>
          <p className="text-xs text-gray-600 mt-1">Images created by this agent will appear here</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {images.map((img) => (
              <GalleryThumb key={img.id} img={img} onClick={() => setLightboxUrl(img.url)} />
            ))}
          </div>
          {hasMore && (
            <div className="flex justify-center py-5">
              <button
                onClick={() => load(offset)}
                className="text-xs font-medium px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
              >
                Load more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── SkillsTab placeholder ────────────────────────────── */
const SkillsTab = ({ agent }) => (
  <div className="flex flex-col items-center justify-center h-40 text-center">
    <Star className="w-8 h-8 text-purple-400/40 mb-3" />
    <p className="text-sm text-gray-500">
      {agent?.skills?.length
        ? `${agent.skills.length} skill${agent.skills.length !== 1 ? 's' : ''} configured`
        : 'No skills configured'}
    </p>
    <p className="text-xs text-gray-600 mt-1">Manage this agent's capabilities from Configure</p>
  </div>
);

/* ─── AgentWorkspace ───────────────────────────────────── */
const NAV_ITEMS = [
  { id: 'chat',      label: 'Chat',      Icon: MessageSquare },
  { id: 'workflows', label: 'Workflows', Icon: GitBranch },
  { id: 'gallery',   label: 'Gallery',   Icon: ImageIcon },
  { id: 'settings',  label: 'Settings',  Icon: Settings },
  { id: 'runs',      label: 'Runs',      Icon: Play },
];

const VALID_SECTIONS = NAV_ITEMS.map((n) => n.id);

export default function AgentWorkspace({ agent, projectId, onBack, backLabel = 'Back to projects', onOpenWorkflow, onCreateWorkflow }) {
  const { currentAccount } = useOrganization();
  const { user } = useAuth();
  const { llmModels, templates, updateAssistant, assistants } = useAssistantContext();
  const { toasts, removeToast, success: toastSuccess, error: toastError } = useToast();

  // La subsección abierta vive en la URL (`?section=`), así el workspace es
  // deep-linkeable y compartible (ej. "Open agent" cae directo en Settings).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSection = searchParams.get('section');
  const [activeTab, setActiveTab] = useState(
    VALID_SECTIONS.includes(urlSection) ? urlSection : 'chat'
  );
  // Corrida abierta dentro de la pestana Runs del agente.
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [settingsKey, setSettingsKey] = useState(0);
  const isChatTab = activeTab === 'chat';

  const selectTab = useCallback(
    (id) => {
      setActiveTab(id);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('section', id);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Si la URL cambia por fuera (deep-link, navegación del modal), seguirla.
  useEffect(() => {
    if (urlSection && VALID_SECTIONS.includes(urlSection) && urlSection !== activeTab) {
      setActiveTab(urlSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSection]);

  const accountId = currentAccount?.id;
  const userId = user?.user_id || user?.id || user?.sub;

  // Always use the live agent from context so we reflect saved changes
  const liveAgent = assistants.find(a => a.id === agent?.id) || agent;

  const chatState = useAgentChat(liveAgent, accountId, userId);
  const bg = avatarBg(liveAgent?.name);
  const modelLabel = liveAgent?.modelLabel || liveAgent?.model_label || liveAgent?.config?.modelLabel || 'Claude Sonnet';
  const skillCount = liveAgent?.skills?.length ?? 0;

  const tabCounts = {
    workflows: null,
    gallery: null,
    skills: skillCount || null,
    runs: null,
    chat: null,
    settings: null,
  };

  // Esta barra es la cabecera de la pantalla en móvil: si no reclama el hueco,
  // el shell dibuja su hamburguesa flotante justo encima del botón de volver.
  const isPrimaryHeader = useHeaderSlot();

  // La tira de pestañas se desplaza, así que hay que traer la activa a la
  // vista al cambiar de una a otra. Sin esto, entrando en Settings la tira se
  // quedaba en su sitio y lo primero que se leía era "…at", el final de Chat.
  const tabStripRef = useRef(null);
  const activeTabRef = useRef(null);
  useEffect(() => {
    const strip = tabStripRef.current;
    const item = activeTabRef.current;
    if (!strip || !item) return;
    const target = item.offsetLeft - strip.clientWidth / 2 + item.offsetWidth / 2;
    const max = strip.scrollWidth - strip.clientWidth;
    strip.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
  }, [activeTab]);

  return (
    // Column on phones (mobile bar over the chat), row from lg (sidebar beside it).
    <div className="flex h-full flex-col overflow-hidden lg:flex-row">
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

      {/* ── Mobile bar — stands in for the left sidebar below lg ── */}
      <div className="flex-shrink-0 border-b border-white/8 bg-white/[0.02] lg:hidden">
        <div className="flex items-center gap-2 px-3 py-2">
          {isPrimaryHeader && <MobileMenuButton className="h-9 w-9 rounded-lg" />}
          <button
            onClick={onBack}
            aria-label={backLabel}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className={`h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg ${bg} flex items-center justify-center text-sm font-bold text-white`}>
            {liveAgent?.avatar
              ? <img src={liveAgent.avatar} alt={liveAgent?.name} className="h-full w-full object-cover" />
              : getInitial(liveAgent?.name)}
          </div>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{liveAgent?.name}</span>
        </div>

        {/* Tabs scroll sideways rather than wrapping or overflowing the page */}
        <div
          ref={tabStripRef}
          className="flex items-center gap-1 overflow-x-auto px-2 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {NAV_ITEMS.map(({ id, label, Icon }) => (
            <button
              key={id}
              ref={activeTab === id ? activeTabRef : undefined}
              onClick={() => selectTab(id)}
              className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === id ? 'bg-primary text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Left Sidebar ── */}
      {/* Hidden below lg — its identity block + nav move into the mobile bar
          above, otherwise 208px here + 256px of history left the chat ~0px. */}
      <aside className="hidden w-52 flex-shrink-0 flex-col border-r border-white/8 bg-white/[0.02] overflow-hidden lg:flex">
        {/* Back + agent identity */}
        <div className="px-4 pt-5 pb-4 border-b border-white/8">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors mb-5"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {backLabel}
          </button>

          {/* Avatar */}
          <div className={`w-14 h-14 rounded-2xl ${bg} flex items-center justify-center text-white text-2xl font-bold mb-3`}>
            {liveAgent?.avatar
              ? <img src={liveAgent.avatar} alt={liveAgent?.name} className="w-full h-full object-cover rounded-2xl" />
              : getInitial(liveAgent?.name)
            }
          </div>

          <h2 className="text-sm font-semibold text-white leading-tight">{liveAgent?.name}</h2>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
            {liveAgent?.description || modelLabel}
          </p>
          <span className="inline-block mt-2 text-[10px] font-medium px-2 py-0.5 rounded-full border bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
            Active
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_ITEMS.map(({ id, label, Icon }) => {
            const count = tabCounts[id];
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => selectTab(id)}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                  active
                    ? 'bg-white/8 text-white font-medium'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-purple-400' : ''}`} />
                <span className="flex-1 text-left">{label}</span>
                {count != null && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                    active ? 'bg-purple-500/30 text-purple-300' : 'bg-white/10 text-gray-500'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ── Center + Rail ── */}
      {/* `relative` is the containing block for the history rail, which overlays
          the chat below lg instead of consuming width. */}
      <div className="relative flex min-h-0 flex-1 min-w-0 overflow-hidden">

        {/* Relative container for animated layers */}
        <div className="relative flex-1 min-w-0 overflow-hidden">

          {/* Chat center — always in DOM, shrinks to bubble when leaving */}
          <div
            className="absolute inset-0 will-change-transform"
            style={{
              transform: isChatTab ? 'scale(1)' : 'scale(0.06)',
              transformOrigin: 'right center',
              borderRadius: isChatTab ? '0px' : '9999px',
              opacity: isChatTab ? 1 : 0,
              transition: 'transform 400ms cubic-bezier(0.4,0,1,1), opacity 350ms, border-radius 400ms',
              pointerEvents: isChatTab ? 'auto' : 'none',
              zIndex: isChatTab ? 1 : 0,
            }}
          >
            <AgentChatCenter agent={liveAgent} chatState={chatState} />
          </div>

          {/* Other tabs content — fades in after chat exits */}
          <div
            className="absolute inset-0 overflow-y-auto p-4 sm:p-6"
            style={{
              opacity: isChatTab ? 0 : 1,
              transform: isChatTab ? 'translateY(6px)' : 'translateY(0)',
              transition: isChatTab
                ? 'opacity 180ms, transform 180ms'
                : 'opacity 280ms 220ms, transform 280ms 220ms',
              pointerEvents: isChatTab ? 'none' : 'auto',
              zIndex: isChatTab ? 0 : 1,
            }}
          >
            {activeTab === 'workflows' && (
              <WorkflowsTab
                projectId={projectId}
                agentId={agent?.id}
                agentName={agent?.name}
                accountId={accountId}
                onOpenWorkflow={onOpenWorkflow}
                onCreateWorkflow={onCreateWorkflow}
              />
            )}
            {activeTab === 'gallery' && (
              <GalleryTab
                agentId={agent?.id}
                agentName={agent?.name}
                accountId={accountId}
              />
            )}
            {activeTab === 'skills' && <SkillsTab agent={liveAgent} />}
            {activeTab === 'settings' && (
              <AssistantConfigureView
                key={settingsKey}
                assistant={liveAgent}
                onBack={() => setActiveTab('chat')}
                onSave={() => {}}
                onUpdate={async (assistantId, data) => {
                  const result = await updateAssistant(assistantId, data);
                  if (result?.success) {
                    toastSuccess('Updated', 'Agent updated successfully');
                    setSettingsKey(k => k + 1);
                  } else {
                    toastError('Error', result?.error?.message || 'Failed to update agent');
                  }
                  return result;
                }}
                llmModels={llmModels || []}
                templates={templates || []}
              />
            )}
            {/* Acotado al agente: estando dentro de "Sol", ver las corridas de
                todo el proyecto no responde la pregunta que trae al usuario aquí. */}
            {activeTab === 'runs' && (
              selectedRunId ? (
                <RunInspector
                  runId={selectedRunId}
                  projectId={projectId}
                  onClose={() => setSelectedRunId(null)}
                />
              ) : (
                <RunsView
                  projectId={projectId}
                  agentId={agent?.id}
                  onSelectRun={(id) => setSelectedRunId(id)}
                />
              )
            )}
          </div>
        </div>

        {/* Right rail — slides in as chat shrinks into it. Desktop only: on a
            phone this 288px docked chat would take the whole screen away from
            the tab you actually opened. */}
        <div
          className="hidden flex-shrink-0 flex-col overflow-hidden lg:flex"
          style={{
            width: isChatTab ? 0 : 288,
            opacity: isChatTab ? 0 : 1,
            transition: isChatTab
              ? 'width 300ms cubic-bezier(0.4,0,1,1), opacity 200ms'
              : 'width 360ms cubic-bezier(0,0,0.2,1) 160ms, opacity 300ms 160ms',
          }}
        >
          <AgentChatRail agent={liveAgent} chatState={chatState} />
        </div>
      </div>
    </div>
  );
}
